"use client"

import Image from "next/image"
import type { EmailBackfill } from "@/schema"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { Calendar, Mail } from "lucide-react"

import { useEmailBackfills } from "@/hooks/api/email_backfills"

import { BackfillRing, type RingMode } from "./backfill-ring"

const CALENDAR_COLOR = "text-sand-600"
const EMAIL_COLOR = "text-slate-600"
const NO_ETA_HINT = "This usually finishes within 24 hours, depending on the size of your mailbox."
const EMAIL_STAGE_LABELS: Record<NonNullable<EmailBackfill["email_stage"]>, string> = {
  waiting: "Waiting",
  preparing: "Preparing…",
  importing: "Importing…",
}

const PROVIDER_LOGOS: Record<EmailBackfill["provider"], { src: string; alt: string }> = {
  google: { src: "/google.svg", alt: "Google" },
  microsoft: { src: "/outlook.svg", alt: "Outlook" },
  exchange: { src: "/exchange.svg", alt: "Exchange" },
}

function formatEta(eta: string | null) {
  if (!eta) return null
  return format(new Date(eta), "MMM d, h:mm a")
}

function calendarMode(backfill: EmailBackfill): RingMode {
  return backfill.calendar_syncing ? "spinner" : "complete"
}

function emailMode(backfill: EmailBackfill): RingMode {
  return backfill.calendar_syncing ? "idle" : "spinner"
}

function cardTitle(backfills: EmailBackfill[]): string {
  if (backfills.length > 1) return "Backfilling your accounts"

  const backfill = backfills[0]
  const parts = [backfill.sync_emails && "email", backfill.sync_calendars && "calendar"].filter(Boolean)
  return `Backfilling your ${parts.join(" & ") || "account"}`
}

function footerText(backfill: EmailBackfill): string {
  const eta = backfill.calendar_syncing ? null : formatEta(backfill.backfill_eta)
  return eta ? `Backfill will finish ${eta}` : NO_ETA_HINT
}

function ProviderLogo({ provider, size = 16 }: { provider: EmailBackfill["provider"]; size?: number }) {
  const logo = PROVIDER_LOGOS[provider]
  return <Image src={logo.src} alt={logo.alt} width={size} height={size} className="flex-none" />
}

function BackfillRings({ backfill }: { backfill: EmailBackfill }) {
  return (
    <span className="flex items-center gap-0.5">
      {backfill.sync_calendars && (
        <BackfillRing mode={calendarMode(backfill)} colorClassName={CALENDAR_COLOR} icon={Calendar} />
      )}
      {backfill.sync_emails && <BackfillRing mode={emailMode(backfill)} colorClassName={EMAIL_COLOR} icon={Mail} />}
    </span>
  )
}

function StatusRow({
  icon: Icon,
  label,
  value,
  active,
}: {
  icon: typeof Calendar
  label: string
  value: string
  active: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="flex items-center gap-2">
        <Icon className={cn("size-4", active ? "text-current" : "text-muted-foreground")} />
        {label}
      </span>
      <span className={cn("font-medium tabular-nums", !active && "text-muted-foreground")}>{value}</span>
    </div>
  )
}

function BackfillBlock({ backfill }: { backfill: EmailBackfill }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <ProviderLogo provider={backfill.provider} size={14} />
        <span className="text-muted-foreground truncate text-xs">{backfill.connected_email}</span>
      </div>
      <div className="space-y-1.5">
        {backfill.sync_calendars && (
          <div className={backfill.calendar_syncing ? CALENDAR_COLOR : undefined}>
            <StatusRow
              icon={Calendar}
              label="Calendar"
              value={backfill.calendar_syncing ? "Syncing…" : "Done"}
              active={backfill.calendar_syncing}
            />
          </div>
        )}
        {backfill.sync_emails && (
          <div className={!backfill.calendar_syncing ? EMAIL_COLOR : undefined}>
            <StatusRow
              icon={Mail}
              label="Email"
              value={EMAIL_STAGE_LABELS[backfill.email_stage ?? "preparing"]}
              active={!backfill.calendar_syncing}
            />
          </div>
        )}
      </div>
      <div className="text-muted-foreground border-t pt-2 text-xs">{footerText(backfill)}</div>
    </div>
  )
}

export function BackfillStatusBadge() {
  const { data: backfills } = useEmailBackfills()

  if (!backfills || backfills.length === 0) return null

  const primary = backfills[0]
  const extraCount = backfills.length - 1

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="hover:bg-accent bg-background flex h-8 items-center gap-2 rounded-md border px-2.5 text-sm transition-colors"
          aria-label="Email backfill progress"
        >
          <ProviderLogo provider={primary.provider} />
          <span className="hidden max-w-[150px] truncate sm:inline">{primary.connected_email}</span>
          <BackfillRings backfill={primary} />
          {extraCount > 0 && (
            <span className="bg-tertiary-foreground text-background flex size-4 items-center justify-center rounded-sm text-[10px]">
              +{extraCount}
            </span>
          )}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-72">
        <p className="text-sm font-medium">{cardTitle(backfills)}</p>
        {backfills.length === 1 ? (
          <div className="mt-2">
            <BackfillBlock backfill={primary} />
          </div>
        ) : (
          <div className="mt-2 divide-y">
            {backfills.map((backfill) => (
              <div key={backfill.id} className="py-3 first:pt-0 last:pb-0">
                <BackfillBlock backfill={backfill} />
              </div>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}
